import { useEffect, useState } from "react";
import { ref, set, get, remove, update} from 'firebase/database';
import { database } from '../firebase_config/firebase';
import { UserAuth } from "../context/AuthContext";
import {showToast} from "../components/toast";
import { toast, ToastContainer } from "react-toastify";
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmDialog from "../components/confirm_dialog";
import LoadingComponent from "../components/loading_backdrop";
import { faPaperPlane, faDownload, faTrash } from '@fortawesome/free-solid-svg-icons';
import MyRatingComponent from "../components/rating_component";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import CardItem from "../components/card-item";
import { Box, Card, CardContent, Container, Divider, Tab, Tabs, Typography, useMediaQuery, useTheme } from "@mui/material";
import SideNav from "../components/side_nav";
import { ContentCopy } from "@mui/icons-material";
import getProjectStatus from "../utils/get-project-status";
import axios from "axios";
import AlertBox from "../components/dashboard-alert-box";
import NewProjectDrawer from "../components/new-project-drawer";
import Dot from "../components/dot";
import CollectedDataTable from "../components/collected-data-table";

const API_URL = process.env.REACT_APP_API_URL;
const FIXED_AMOUNT = 25350;

const Dashboard = () =>{
  const { project_unique_id } = useParams();
  const { user } = UserAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [project, setProject] = useState({});

    
  const navigate = useNavigate();
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEndDialogOpen, setIsEndDialogOpen] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [showDialogForSentAnalysis, setShowDialogForSentAnalysis] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rated, setRated] = useState(false);

  const [activeTab, setActiveTab] = useState(0);

  const [drawer, setDrawer] = useState({
    open: false,
    action: ''
  });


  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };
      
  const copy_to_clipboard = (data_to_copy) =>{
    const textarea = document.createElement('textarea');
    textarea.value = data_to_copy;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    showToast("copied", true);
  }

  const sendForAnalysis = () => {
    if (!project) {
      toast.error('an error occured');
      return;
    }

    if (Object.keys(project.collected_data || {}).length < 1) {
      toast.warning('collect some data and try again')
      return;
    }

    if(project.status !== 'RUNNING') {
      toast.warning('project not running');
      return;
    }

    if (project.amount_paid < FIXED_AMOUNT) {
      toast.warning('outstanding balance detected');
      return;
    }
    setIsEndDialogOpen(true);
  };

  const onAnalysisSendConfirm = async () =>{
    try{
      setIsEndDialogOpen(false);
      setIsLoading(true);

      const project_status_ref = ref(database, 'Users/' + user.uid + '/Projects/' + project_unique_id);
      const awaiting_result_ref = ref(database, 'AwaitingResults/' + project_unique_id);

      await Promise.all([
        set(awaiting_result_ref, {
          user_id: user.uid,
          project_id: project_unique_id,
        }),
        update(project_status_ref, {
          url: "",
          status: 'AWAITING_RESULT'
        }),
        axios.post(`${API_URL}/email/result-requested`, {
          recipient: 'result-admin',
          project_id: project_unique_id
        })
      ]);

      setProject((prev) => ({
        ...prev,
        url: '',
        status: 'AWAITING_RESULT'
      }));
      
      toast.success('data sent for analysis');
    }catch(e){
      toast.error(e.message, false);
    } finally {
      setIsLoading(false);
    }
  };

  
  const downloadResult = () =>{
    if(!project.result_url){
      toast.warning('result not available yet');
      return;        
    }

    const link = document.createElement('a');
    link.href = project.result_url;
    link.target = '_blank';
    link.download = project.result_url.substring(project.result_url.lastIndexOf('/') + 1);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onDeleteRows = async (rows) => {
    for (const element of rows) {
      const dataRef = ref(database, "Users/" + user.uid + "/Projects/" + project_unique_id + '/collected_data/' + element);
      await remove(dataRef);
    }

    setProject((prev) => {
      const updatedData = { ...prev.collected_data };

      rows.forEach(key => {
        delete updatedData[key];
      });

      return {
        ...prev,
        collected_data: updatedData,
      };
    });
  };

  const deleteProject = async () => {
    if (!project) {
      toast.error('an error occcured');
      return;
    }

    if (project.status !== 'COMPLETED' && project.status !== "CREATED") {
      toast.warning('project is still active');
      return;
    }
    
    setDeleteDialogOpen(true);
  };

  const onDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    setIsLoading(true);
    const dataRef = ref(database, "Users/" + user.uid + "/Projects/" + project_unique_id);
    await remove(dataRef);
    setIsLoading(false);
    navigate('/home')
  };
    

  useEffect(() => {
    let isMounted = true;
    const usersRef = ref(database, 'Users/' + user.uid + '/Projects/' + project_unique_id);
  
    const get_project = async () => {
      try {
        const snapshot = await get(usersRef);
  
        if (snapshot.exists()) {
          const data = snapshot.val();

          if (isMounted) {
            setProject({
              id: data.id,
              username: data.username,
              email: data.email,
              phone: data.phone,
              name: data.name,
              type: data.type,
              status: data.status,
              url: data.url || '',
              amount_paid: data.amount_paid,
              sample_size: data.sample_size || '',
              file_url: data.file_url || '',
              result_url: data.result_url || '',
              collected_data: data.collected_data ? data.collected_data : {},
              timestamp: data.timestamp,
            });
            setIsLoading(false);
          }
        } else {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      } catch (error) {
        if (isMounted) {
          toast.error('an error occured');         
          setIsLoading(false);
        }
      }
    };
  
    get_project();
  
    return () => {
      isMounted = false;
    };
  }, [user.uid, project_unique_id]);
  
    
  useEffect(() => {
    const storedValue = localStorage.getItem(project_unique_id);
    if (storedValue) {
      setRated(storedValue);
    }
  }, []);
  
  const ProjectLayout = () => {
    return (
      <Container sx={{ mt: isMobile ? 3 : 13, mb: 5}}>

          {!isMobile && <AlertBox userId={user.uid} project={project} setDrawer={setDrawer}/>}

          <Box 
            sx={{ 
              marginTop: isMobile ? '' : 4,
              display: 'flex', 
              flexDirection: {xs: 'column', md: 'row'},
              // border: isMobile ? '' : '1px solid #000000',
              padding: isMobile ? '' : 3,
              borderRadius: isMobile ? '' : 3
            }}
          >
            <Card
              sx={{
                backgroundColor: '#081247',
                color: '#fff',
                borderRadius: '12px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                width: {xs: '100%', md: '50%'},
                justifyContent: 'center',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ color: "#FAED7A", fontWeight: 'bold' }}>
                      {Object.keys(project.collected_data || {}).length || 0}
                    </Typography>
                    <Typography variant="subtitle2">
                      Received Data
                    </Typography>
                  </Box>

                  <Divider sx={{ borderColor: "#fff", my: 2 }}/>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ flexGrow: 1, color: "#FAED7A", fontSize: '0.8rem', textAlign: 'center' }}>
                      {project.url ? (
                        project.url
                      ) : !project.url && project.status === 'AWAITING_URL' ? (
                        <>
                          <Dot delay="0s" />
                          <Dot delay="0.2s" />
                          <Dot delay="0.4s" />
                        </>
                      ) : (
                        '********'
                      )}
                    </Typography>

                    {project.url && (
                      <ContentCopy
                        sx={{ color: "#CCCCCC", fontSize: '1rem', ml: 1, cursor: 'pointer' }}
                        onClick={() => copy_to_clipboard(project.url)}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ textAlign: 'center' }}>
                    Data Collection URL
                  </Typography>
                  <Divider sx={{ borderColor: "#fff", my: 2 }}/>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ flexGrow: 1, color: "#FAED7A", fontSize: '0.8rem', textAlign: 'center' }}>
                      {project.id}
                    </Typography>
                    <ContentCopy
                      sx={{ color: "#CCCCCC", fontSize: '1rem', ml: 1, cursor: 'pointer' }}
                      onClick={() => copy_to_clipboard(project.url)}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ textAlign: 'center' }}>
                    Project ID
                  </Typography>
                  <Divider sx={{ borderColor: "#fff", my: 2 }}/>
            

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ color: '#FAED7A' }}>
                    {projectStatus.label}
                  </Typography>
                  <Typography variant="body2">
                    Status
                  </Typography>
                </Box>

              </CardContent>
            </Card>
            
            {isMobile && (<AlertBox userId={user.uid} project={project} setDrawer={setDrawer}/>)}

            <Grid2 container spacing={2} sx={{ p: {md:2}, mt: {xs: 2}}}>
              <Grid2 item xs={12}>
                  <CardItem img={faPaperPlane} title="Send For Analysis" color="#308a19" onClick={sendForAnalysis}/>
                </Grid2>

              <Grid2 item xs={12}>
                <CardItem img={faDownload} title="Download Result" color="#FF8C00" onClick={downloadResult}/>
              </Grid2>

              <Grid2 item xs={12}>
                <CardItem img={faTrash} title="Delete Project" color="#8B0000" onClick={deleteProject}/>
              </Grid2>
            
            </Grid2>

          </Box>

          {!isMobile && <CollectedDataTable loading={isLoading} collectedData={project.collected_data} onDeleteRows={(rows) => onDeleteRows(rows)}/>}
        </Container>
    );
  }
    
    
  const projectStatus = getProjectStatus(project.status);

  return(
    <Box sx={{ backgroundColor: '#fff' }}>
        {!isLoading && project.status === 'COMPLETED' && !rated &&
          <div className="fab">
              <MyRatingComponent user_id={user.uid} unique_id={project_unique_id}/>
          </div>
        }

        {isLoading && <LoadingComponent/>}
        
      
        <SideNav/>

        

        {isMobile ? (
          <>
            <Box 
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider', 
                flexShrink: 0 ,
                mt: 10
              }}
            >
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        minHeight: 48,
                        '& .MuiTab-root': {
                            minHeight: 48,
                            fontSize: '0.875rem',
                            fontWeight: 500
                        }
                    }}
                >
                    <Tab 
                        label="Overview"
                        sx={{ textTransform: 'none' }}
                    />
                    <Tab 
                        label="Collected Data" 
                        sx={{ textTransform: 'none' }}
                    />
                </Tabs>
            </Box>

            {activeTab === 0 && <ProjectLayout/>}

            {activeTab === 1 && (
              <CollectedDataTable 
                loading={isLoading} 
                collectedData={project.collected_data} 
                onDeleteRows={(rows) => onDeleteRows(rows)}
              />
            )}
          
          </>
        ) : (
          <ProjectLayout/>
        )}

        


        <ToastContainer/>

        
          
        <NewProjectDrawer 
          open={drawer.open}
          onClose={() => setDrawer({
            open: false,
            action: ''
          })}
          currentProject={project}
          action={drawer.action}
          onProjectCreated={(updatedProject) => setProject(updatedProject)}
        />

        <ConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onProceed={onDeleteConfirm}
          description={"By clicking \"Delete\", you confirm that this project is no longer relevant to you"}
        />

        <ConfirmDialog
          open={isEndDialogOpen}
          onClose={() => setIsEndDialogOpen(false)}
          onProceed={onAnalysisSendConfirm}
          description={`We will now proceed to running your analysis we will contact you via 
            ${project.phone} or ${project.email} once completed or encounter any error.`
          }
        />    
      </Box>
  );
}

export default Dashboard;